import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useWallet } from '@solana/wallet-adapter-react'
import { useWalletAuth } from './WalletAuthProvider'

const DisplayStoreContext = createContext()

export function DisplayStoreProvider({ children }) {
  const { pathname } = useLocation()
  const onTrade = pathname.startsWith('/trade')
  const { isConnected: evmConnected } = useWalletAuth()
  const { connected: solConnected } = useWallet()
  const walletConnected = Boolean(evmConnected || solConnected)
  const [selectedMarket, setSelectedMarket] = useState('LIT')
  const [isMarketSelectorOpen, setIsMarketSelectorOpen] = useState(false)
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false)
  const [isWhatsNewOpen, setIsWhatsNewOpen] = useState(false)
  const [orderData, setOrderData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [balance, setBalance] = useState([])

  const openMarketSelector = useCallback(() => setIsMarketSelectorOpen(true), [])
  const closeMarketSelector = useCallback(() => setIsMarketSelectorOpen(false), [])
  const openConnectModal = useCallback(() => setIsConnectModalOpen(true), [])
  const closeConnectModal = useCallback(() => setIsConnectModalOpen(false), [])
  const openWhatsNew = useCallback(() => {
    if (!walletConnected) return
    setIsWhatsNewOpen(true)
  }, [walletConnected])
  const closeWhatsNew = useCallback(() => {
    setIsWhatsNewOpen(false)
    try {
      localStorage.setItem('lighter:whats-new-v3-seen', '1')
    } catch {
      /* ignore quota / private mode */
    }
  }, [])

  useEffect(() => {
    if (!walletConnected) {
      setIsWhatsNewOpen(false)
      return undefined
    }
    if (!onTrade) return undefined
    try {
      if (localStorage.getItem('lighter:whats-new-v3-seen')) return undefined
    } catch {
      return undefined
    }
    const timer = window.setTimeout(() => setIsWhatsNewOpen(true), 480)
    return () => window.clearTimeout(timer)
  }, [onTrade, walletConnected])

  useEffect(() => {
    const onOpenConnect = () => setIsConnectModalOpen(true)
    const onOpenWhatsNew = () => {
      if (walletConnected) setIsWhatsNewOpen(true)
    }
    const onOpenMarket = () => setIsMarketSelectorOpen(true)
    window.addEventListener('lighter:open-connect', onOpenConnect)
    window.addEventListener('lighter:open-whats-new', onOpenWhatsNew)
    window.addEventListener('lighter:open-market-selector', onOpenMarket)
    return () => {
      window.removeEventListener('lighter:open-connect', onOpenConnect)
      window.removeEventListener('lighter:open-whats-new', onOpenWhatsNew)
      window.removeEventListener('lighter:open-market-selector', onOpenMarket)
    }
  }, [walletConnected])

  const value = {
    selectedMarket,
    setSelectedMarket,
    isMarketSelectorOpen,
    openMarketSelector,
    closeMarketSelector,
    isOrderModalOpen,
    setIsOrderModalOpen,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    isConnectModalOpen,
    setIsConnectModalOpen,
    isWhatsNewOpen,
    setIsWhatsNewOpen,
    openConnectModal,
    closeConnectModal,
    openWhatsNew,
    closeWhatsNew,
    orderData,
    setOrderData,
    isLoading,
    setIsLoading,
    balance,
    setBalance,
  }

  return (
    <DisplayStoreContext.Provider value={value}>
      {children}
    </DisplayStoreContext.Provider>
  )
}

export function useDisplayStore() {
  const context = useContext(DisplayStoreContext)
  if (!context) {
    throw new Error('useDisplayStore must be used within DisplayStoreProvider')
  }
  return context
}
